import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';
import useScrollLock from '@/hooks/useScrollLock';
import { proxiedImage } from '@/utils/proxiedImage';

import { logger } from '@/utils/logger';
import { ArrowLeft, ExternalLink, Heart, Info, ShoppingCart, Sparkles, Store, User, X } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import WishlistSelectorModal from '@/components/WishlistSelectorModal';
import { AIRecommendationBanner } from '@/components/product/AIRecommendationBanner';
import RecipientSelectorModal from '@/components/RecipientSelectorModal';
import ImageCarousel from '@/components/ImageCarousel';
import PurchaseExplainer, { shouldShowPurchaseExplainer } from '@/components/browse/PurchaseExplainer';
export default function ProductModal({ product: initialProduct, onClose, user, aiMetadata }) {
  const darkMode = useDarkMode();
  const [showExplainer, setShowExplainer] = useState(() => shouldShowPurchaseExplainer());
  const [currentProduct, setCurrentProduct] = useState(initialProduct);
  const [history, setHistory] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showWishlistSelector, setShowWishlistSelector] = useState(false);
  const scrollContainerRef = useRef(null);
  
  // Get images array - prefer the canonical `image_url` as the lead image
  // (see ProductCard.jsx for context on the seeded duplicate-image issue).
  // Each URL is proxied through the backend so the browser only fetches from
  // our own domain, sidestepping CDN hosts that some networks block.
  const productImages = (() => {
    let urls;
    if (currentProduct.image_url && currentProduct.images && currentProduct.images.length > 0) {
      const rest = currentProduct.images.filter((u) => u !== currentProduct.image_url);
      urls = [currentProduct.image_url, ...rest];
    } else if (currentProduct.images && currentProduct.images.length > 0) {
      urls = currentProduct.images;
    } else {
      urls = currentProduct.image_url ? [currentProduct.image_url] : [];
    }
    return urls.map(proxiedImage);
  })();

  // Lock viewport scroll while the modal is open (shared html-level lock).
  useScrollLock(true);

  const handleSaveToKeepsakes = () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('mementa:open-auth'));
      toast.info('Sign in to save gifts');
      return;
    }
    setShowRecipientSelector(true);
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('mementa:open-auth'));
      toast.info('Sign in to save gifts');
      return;
    }
    
    try {
      const isCurrentlyInWishlist = currentProduct.isInWishlist;
      
      if (isCurrentlyInWishlist) {
        await api.delete(`/wishlist/${currentProduct.id}`);
        setCurrentProduct({...currentProduct, isInWishlist: false});
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/wishlist/${currentProduct.id}`);
        setCurrentProduct({...currentProduct, isInWishlist: true});
        toast.success('Added to wishlist');
      }
    } catch (error) {
      logger.error('Error toggling wishlist:', error);
      toast.error('Failed to update wishlist');
    }
  };

  const handleCheckout = () => {
    const link = currentProduct.merchant_link || currentProduct.link;
    if (!link) return;
    // Fire-and-forget tracking; don't block the user even if the request fails
    try {
      api.post(`/products/${currentProduct.id}/track`, { event_type: 'click', source: 'modal' })
        .catch(() => {});
    } catch { /* ignore */ }
    window.open(link, '_blank');
  };

  const handleRelatedProductClick = (relatedProduct) => {
    // Start transition
    setIsTransitioning(true);
    
    // Remember the product we're leaving so the user can go back to it
    setHistory((prev) => [...prev, currentProduct]);
    
    // Update product immediately
    setCurrentProduct(relatedProduct);
    
    // Scroll to top of modal after a brief moment
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }
      // End transition after scroll starts
      setTimeout(() => {
        setIsTransitioning(false);
      }, 400);
    }, 100);
  };

  const handleBack = () => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    setIsTransitioning(true);
    setHistory((prev) => prev.slice(0, -1));
    setCurrentProduct(previous);
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setTimeout(() => setIsTransitioning(false), 400);
    }, 100);
  };

  // Fetch related products based on category and interests
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      setLoadingRelated(true);
      try {
        const response = await api.get('/products');
        const allProducts = response.data;
        
        // Filter related products based on category, interests, or personality
        const related = allProducts
          .filter(p => p.id !== currentProduct.id) // Exclude current product
          .filter(p => {
            // Match category
            if (p.category === currentProduct.category) return true;
            
            // Match interests
            const commonInterests = p.interests?.filter(i => 
              currentProduct.interests?.includes(i)
            ) || [];
            if (commonInterests.length > 0) return true;
            
            // Match personality types
            const commonPersonality = p.personality_types?.filter(pt => 
              currentProduct.personality_types?.includes(pt)
            ) || [];
            if (commonPersonality.length > 0) return true;
            
            return false;
          })
          .slice(0, 8); // Show up to 8 related products
        
        setRelatedProducts(related);
      } catch (error) {
        logger.error('Failed to fetch related products:', error);
      } finally {
        setLoadingRelated(false);
      }
    };

    fetchRelatedProducts();
  }, [currentProduct]);

  return createPortal((
    <div 
      className="fixed inset-0 flex items-start justify-center p-4 animate-in fade-in duration-300 overflow-y-auto"
      style={{
        background: darkMode.isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
        zIndex: 99999,
        pointerEvents: 'auto'
      }}
      onClick={onClose}
    >
      <div 
        role="dialog"
        aria-modal="true"
        data-testid="product-modal"
        className="relative w-full max-w-6xl my-0 md:my-8 md:rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden transition-colors"
        style={{
          background: darkMode.isDark ? '#2D4354' : 'white',
          maxHeight: '100vh',
          borderRadius: '0',
          border: darkMode.isDark ? '2px solid #FED7A5' : 'none',
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Back Button, only when the user navigated in via "You May Also Like" */}
        {history.length > 0 && (
          <button
            onClick={handleBack}
            data-testid="modal-back-button"
            className="absolute top-4 left-4 z-50 flex items-center gap-1.5 pl-2.5 pr-3.5 py-2 rounded-full shadow-lg hover:scale-105 transition-all text-sm font-semibold"
            style={{
              background: darkMode.isDark ? '#FED7A5' : 'white',
              color: '#20212B',
              opacity: 0.95
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}

        {/* Heart / Save Button, top-right, just left of the close button */}
        <button
            onClick={(e) => {
              e.stopPropagation();
              if (!user) {
                window.dispatchEvent(new CustomEvent('mementa:open-auth'));
                toast.info('Sign in to save gifts');
                return;
              }
              setShowSaveModal(true);
            }}
            data-testid="modal-heart-button"
            className="absolute top-4 right-16 z-50 p-2 rounded-full shadow-lg hover:scale-110 transition-all"
            style={{
              background: darkMode.isDark ? '#FED7A5' : 'white',
              opacity: 0.9
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.9'}
          >
            <Heart 
              className="w-6 h-6" 
              style={{ 
                color: currentProduct.isInWishlist ? '#9E6752' : '#534145',
                fill: currentProduct.isInWishlist ? '#9E6752' : 'none'
              }} 
            />
          </button>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 rounded-full shadow-lg hover:scale-110 transition-all"
          style={{
            background: darkMode.isDark ? '#FED7A5' : 'white',
            color: darkMode.isDark ? '#20212B' : '#20212B',
            opacity: 0.9
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.9'}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Transition Loading Indicator */}
        {isTransitioning && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{borderColor: '#9E6752', borderTopColor: 'transparent'}}></div>
          </div>
        )}

        {/* Scrollable Content */}
        <div 
          ref={scrollContainerRef}
          className="overflow-y-auto scrollbar-hide" 
          style={{
            maxHeight: '100vh',
            scrollbarWidth: 'none', /* Firefox */
            msOverflowStyle: 'none'  /* IE and Edge */
          }}
        >
          {/* Main Product Section with Transition */}
          <div 
            className={`px-0 md:px-6 lg:px-8 py-0 md:py-8 transition-opacity duration-300 ${isTransitioning ? 'opacity-30' : 'opacity-100'}`}
          >
            <div className="grid md:grid-cols-2 gap-0 md:gap-8 mb-6 md:mb-12">
          {/* Left: Product Image Gallery - Full width on mobile, extends to top */}
          <div className="relative">
            <div 
              className="w-full aspect-square md:rounded-xl overflow-hidden transition-colors"
              style={{
                background: darkMode.isDark ? '#1a1b23' : '#f3f4f6',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <ImageCarousel 
                images={productImages}
                alt={currentProduct.name}
                className="md:rounded-xl"
                showIndicators={true}
                showCounter={true}
              />
            </div>

            {/* Heart Button - Top Right of Image */}
            {/* heart moved to top-right of the modal, left of the X close button */}
          </div>

          {/* Right: Product Details */}
          <div className="flex flex-col px-6 md:px-0 mt-6 md:mt-0">
            {/* Product Name */}
            <div className="mb-3">
              <h2 
                className="text-3xl sm:text-4xl leading-tight transition-colors md:pr-24"
                style={{
                  fontFamily: 'Playfair Display, serif',
                  color: darkMode.textPrimary,
                  fontWeight: '600'
                }}
              >
                {currentProduct.name}
              </h2>
            </div>

            {/* Category Badge (+ interest tags) & Price - Same Line */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex flex-wrap items-center gap-2">
                <span 
                  className="inline-block px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{background: '#FED7A5', color: '#20212B'}}
                >
                  {currentProduct.category}
                </span>
                {currentProduct.interests && currentProduct.interests.length > 0 && (
                  currentProduct.interests.slice(0, 3).map((interest, idx) => (
                    <div 
                      key={`${currentProduct.id}-interest-${interest}-${idx}`}
                      className="px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors"
                      style={{
                        background: darkMode.isDark ? 'rgba(254, 215, 165, 0.2)' : '#F5F5F5',
                        color: darkMode.isDark ? '#FED7A5' : '#534145'
                      }}
                    >
                      <Sparkles className="w-3 h-3" />
                      <span>{interest}</span>
                    </div>
                  ))
                )}
              </div>
              
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <div className="flex items-baseline gap-2">
                  <span 
                    className="text-3xl sm:text-4xl font-bold"
                    style={{color: '#9E6752'}}
                  >
                    ${currentProduct.price}
                  </span>
                  <span className="text-sm text-gray-500">USD</span>
                </div>
                {currentProduct.stock_status === 'out_of_stock' && (
                  <span
                    data-testid="product-modal-oos-badge"
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ background: '#534145', color: '#FED7A5' }}
                  >
                    Out of stock
                  </span>
                )}
              </div>
            </div>

            {/* AI Recommendation Section */}
            <AIRecommendationBanner aiMetadata={aiMetadata} darkMode={darkMode} />

            {/* Description */}
            <div className="mb-6">
              <p className="text-base leading-relaxed transition-colors" style={{color: darkMode.textSecondary}}>
                {currentProduct.description}
              </p>
            </div>

            {/* Merchant Info */}
            <div 
              className="mb-6 px-4 py-2.5 rounded-xl flex items-center gap-2.5 transition-colors"
              style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#FFFBF7',
                border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#E5E5E5'}`
              }}
            >
              <Store className="w-5 h-5 flex-shrink-0" style={{color: darkMode.isDark ? '#FED7A5' : '#9E6752'}} />
              <p className="text-sm transition-colors" style={{color: darkMode.textSecondary}}>
                Sold by{' '}
                <span className="font-semibold" style={{color: darkMode.isDark ? '#FED7A5' : '#9E6752'}}>
                  {currentProduct.merchant}
                </span>
              </p>
            </div>

            {/* Occasions */}
            {currentProduct.occasions && currentProduct.occasions.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium mb-2 transition-colors" style={{color: darkMode.textSecondary}}>
                  Perfect for:
                </p>
                <div className="flex flex-wrap gap-2">
                  {currentProduct.occasions.map((occasion, idx) => (
                    <div 
                      key={`${currentProduct.id}-occasion-${occasion}-${idx}`}
                      className="px-3 py-1.5 rounded-lg text-[15px] font-semibold tracking-wide"
                      style={{background: darkMode.isDark ? '#FED7A5' : '#2D4354', color: darkMode.isDark ? '#20212B' : 'white'}}
                    >
                      {occasion}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Personality Types */}
            {currentProduct.personality_types && currentProduct.personality_types.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium mb-2 transition-colors" style={{color: darkMode.textSecondary}}>
                  Great for:
                </p>
                <div className="flex flex-wrap gap-2">
                  {currentProduct.personality_types.map((type, idx) => (
                    <div 
                      key={`${currentProduct.id}-personality-${type}-${idx}`}
                      className="px-3 py-1.5 rounded-lg text-[15px] font-semibold tracking-wide"
                      style={{background: '#FED7A5', color: '#20212B'}}
                    >
                      {type}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-2">
              <Button
                onClick={handleCheckout}
                className="w-full flex items-center justify-center gap-2 py-6 text-base font-semibold transition-all hover:scale-105"
                style={{background: '#9E6752', color: 'white', borderRadius: '12px'}}
              >
                <ShoppingCart className="w-5 h-5" />
                <span>View on {currentProduct.merchant}</span>
                <ExternalLink className="w-4 h-4" />
              </Button>
              <p
                data-testid="product-affiliate-disclosure"
                className="mt-2.5 text-xs leading-relaxed text-center"
                style={{ color: darkMode.isDark ? 'rgba(255,255,255,0.55)' : '#9CA3AF' }}
              >
                This is an affiliate link. Mementa may earn a commission from qualifying purchases,
                at no additional cost to you. Details may be AI-assisted; confirm price and
                availability with the merchant.
              </p>
              <button
                onClick={() => setShowExplainer(true)}
                className="w-full mt-2 flex items-center justify-center gap-1.5 text-sm py-1 transition-colors hover:underline"
                style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
                data-testid="how-buying-works-link"
              >
                <Info className="w-3.5 h-3.5" />
                How buying works
              </button>
            </div>

            {!user && (
              <p className="mt-3 text-sm text-center transition-colors" style={{color: darkMode.textSecondary}}>
                Sign in to save products to your wishlist
              </p>
            )}
          </div>
            </div>

            {/* Divider */}
            <div className="my-8 mx-6 md:mx-0" style={{borderTop: '1px solid #E5E5E5'}} />

            {/* Related Products Section */}
            <div className="pb-8 px-6 md:px-0">
              <div className="mb-6">
                <h3 
                  className="text-2xl sm:text-3xl mb-2 transition-colors"
                  style={{fontFamily: 'Playfair Display, serif', color: darkMode.isDark ? '#FED7A5' : darkMode.textPrimary, fontWeight: '600'}}
                >
                  You May Also Like
                </h3>
                <p className="text-sm sm:text-base transition-colors" style={{color: darkMode.isDark ? '#E5E5E5' : darkMode.textSecondary}}>
                  Similar products based on this item
                </p>
              </div>

              {loadingRelated ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-base transition-colors" style={{color: darkMode.textSecondary}}>Loading recommendations...</div>
                </div>
              ) : relatedProducts.length > 0 ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-5 sm:gap-x-4 sm:gap-y-6">
                  {relatedProducts.map((relatedProduct) => (
                    <div key={relatedProduct.id} data-testid={`related-product-${relatedProduct.id}`}>
                      <ProductCard 
                        product={relatedProduct} 
                        user={user}
                        onQuickView={(p) => { if (!isTransitioning) handleRelatedProductClick(p); }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" style={{color: '#534145'}}>
                  <p>No similar products found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Recipient Selector Modal */}
      {showRecipientSelector && (
        <RecipientSelectorModal
          product={currentProduct}
          user={user}
          onClose={() => setShowRecipientSelector(false)}
        />
      )}

      {/* Wishlist Selector Modal */}
      {showWishlistSelector && (
        <WishlistSelectorModal
          isOpen={showWishlistSelector}
          onClose={() => setShowWishlistSelector(false)}
          productId={currentProduct.id}
          onSuccess={() => {
            setShowWishlistSelector(false);
            setCurrentProduct({...currentProduct, isInWishlist: true});
            // Toast is already shown in WishlistSelectorModal
          }}
        />
      )}

      {/* Save Modal - Choose Wishlist or Keepsake */}
      {showSaveModal && (
        <div 
          className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
          style={{
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowSaveModal(false)}
        >
          <div 
            className="relative w-full max-w-sm rounded-2xl shadow-2xl p-6"
            style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}>
              Save Gift
            </h3>
            <p className="text-sm mb-6" style={{ color: darkMode.textSecondary }}>
              Where would you like to save this gift?
            </p>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setShowRecipientSelector(true);
                }}
                className="w-full p-4 rounded-xl flex items-center gap-3 transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #FED7A5 0%, #E8C795 100%)',
                  boxShadow: '0 4px 12px rgba(254, 215, 165, 0.3)'
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                  <User className="w-5 h-5" style={{ color: '#9E6752' }} />
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold" style={{ color: '#20212B' }}>Save for Someone</div>
                  <div className="text-xs" style={{ color: '#534145' }}>Add to a keepsake profile</div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setShowWishlistSelector(true);
                }}
                className="w-full p-4 rounded-xl flex items-center gap-3 transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #E8B4B8 0%, #D4A4A8 100%)',
                  boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)'
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                  <Heart className="w-5 h-5" style={{ color: '#9E6752' }} />
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold" style={{ color: '#20212B' }}>My Wishlists</div>
                  <div className="text-xs" style={{ color: '#534145' }}>Choose a wishlist to save to</div>
                </div>
              </button>

              <button
                onClick={() => setShowSaveModal(false)}
                className="w-full p-3 rounded-xl text-center transition-all hover:bg-opacity-80"
                style={{ 
                  background: darkMode.isDark ? 'rgba(255,255,255,0.1)' : '#F3F4F6',
                  color: darkMode.textPrimary 
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showExplainer && <PurchaseExplainer onDismiss={() => setShowExplainer(false)} />}
    </div>
  ), document.body);
}
